import { useEffect, useState } from "react";
import { DatePicker, Table } from "antd";
import { Dayjs } from "dayjs";
import Column from "antd/es/table/Column";
import { useGetAllAccommodationMutation, useGetAccommodationExcelDataMutation } from "apis/services/arrival";
import { useNavigate, useSearchParams } from "react-router-dom";
import columns from "components/AddNewArrivalTables/Columns/Accommodation";
import DebouncedSearch from "components/inputs/DebouncedSearch";
import { CiSearch } from "react-icons/ci";
import COLORS from "constants/Colors";
import Icon from "components/general/Icon";
import Button from 'components/general/Button/index';
import ExportDataModal from "components/modals/ExportData";

import downloadFromBlob from "utils/downloadFromBlob";
import styles from "./styles.module.scss";

type RangeValue = [Dayjs | null, Dayjs | null] | null;
const { RangePicker } = DatePicker;

export default function Accommodation() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [getAllAccommodation, { isLoading: isGetAllAccommodationLoading }] =
    useGetAllAccommodationMutation();
  const [allAccommodation, setAllAccommodation] = useState([]);
  const [allAccommodationCount, setAllAccommodationCount] = useState(0);
  const [getAccommodationExcelData, { isLoading: isGetAccommodationExcelDataLoading }] = useGetAccommodationExcelDataMutation();
  const [isExportDataModalVisible, setIsExportDataModalVisible] = useState(false);
  const searchTerm = searchParams.get("search") ?? "";
  const [dateRange, setDateRange] = useState<RangeValue>(null);

  useEffect(() => {
    let check_in_after: string | undefined;
    let check_in_before: string | undefined;

    if (dateRange && dateRange.length === 2) {
      check_in_after = dateRange[0]?.format("YYYY-MM-DD");
      check_in_before = dateRange[1]?.format("YYYY-MM-DD");
    }

    getAllAccommodation({
      search: searchTerm ?? "",
      page: searchParams.get("accommodationPage") || "1",
      page_size: 50,
      check_in_after,
      check_in_before,
    })
      .unwrap()
      .then((res) => {
        setAllAccommodation(res.results);
        setAllAccommodationCount(res.count);
      });
  }, [searchParams, allAccommodationCount, dateRange]);
  const handleClickRow = (record: any) => {
    navigate(`/Dashboard/reservation-details?id=${record.arrival}`);
  };

  const adjustedColumns = columns.map((column) => {
    switch (column.dataIndex) {
      case "accByWho":
        return { ...column, dataIndex: "by" };
      case "accGuestName":
        return { ...column, dataIndex: ["member", "name"] };
      case "accCity":
        return { ...column, dataIndex: ["city", "name"] };
      case "accType":
        return { ...column, dataIndex: "type"};
      case "hotelName":
        return { ...column, dataIndex: ["hotel_cruise", "name"] };
      case "guideInVisits":
        return { ...column, dataIndex: ["guide", "username"] };
      case "currency":
        return { ...column, dataIndex: ["currency", "name"] };
      case "status":
        return {
          ...column,
          render: (text: any) => {
            const statusStyle =
              text === "waiting" ? styles.waiting : styles.booked;
            return <span className={statusStyle}>{text}</span>;
          },
        };
      default:
        return column;
    }
  });

  return (
    <div>
      <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
        <DebouncedSearch
          onSearchChange={(v) => {
            searchParams.set("search", v);
            setSearchParams(searchParams);
          }}
          containerStyle={styles.tableInput}
          placeholder="Search"
          inputPrefix={<CiSearch color={COLORS.Neutral700} size={26} />}
        />
        <RangePicker value={dateRange} onChange={setDateRange} />
      </div>
      <ExportDataModal
          onCancel={()=>{}}
          onConfirm={(format)=>{
            let check_in_after: string | undefined;
            let check_in_before: string | undefined;

            if (dateRange && dateRange.length === 2) {
              check_in_after = dateRange[0]?.format("YYYY-MM-DD");
              check_in_before = dateRange[1]?.format("YYYY-MM-DD");
            }

            getAccommodationExcelData({ 
              file_type: format,
              search: searchTerm ?? "",
              check_in_after,
              check_in_before,
             }).unwrap().then((res) => {
              downloadFromBlob(res, `Accommodation.${format}`)
            });
          }}
          isVisible={isExportDataModalVisible}
          setIsVisible={setIsExportDataModalVisible}
          titleLabel="Accommodations"
      />

     <div style={{ marginTop: "20px" }} >
      <div style={{display: "flex", justifyContent: "flex-end", marginBottom: "20px"}}  >
        <Button disabled={isGetAccommodationExcelDataLoading} onClick={() => setIsExportDataModalVisible(true)}>
            Export <Icon name="arrow-down" size={24} />
          </Button>
        </div>
       <Table
        dataSource={allAccommodation}
        loading={isGetAllAccommodationLoading}
        scroll={{ x: "max-content" }}
        onRow={(record, rowIndex) => ({
          onClick: () => {
            handleClickRow(record);
          },
        })}
        className={styles.cursorPointer}
        sticky={{ offsetHeader: 0 }}
        pagination={{
          onChange: (page) => {
            searchParams.set("accommodationPage", String(page));
            setSearchParams(searchParams);
          },
          total: allAccommodationCount,
          pageSize: 50,
        }}
      >
        {adjustedColumns.map((column) => (
          <Column
            title={column.title}
            dataIndex={column.dataIndex}
            key={column.key}
            render={column.render}
            width={column.width}
          />
        ))}
      </Table>
     </div>
     
    </div>
  );
}